import { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import Swal from 'sweetalert2'

import {
	TApi,
	TRoutes,
	TSupporterRoles,
	TSupporterStatus,
	StateOptions,
	TContactModes,
	TContactTimes,
	CONSENT_WITHDRAWN_PARAM
} from '../../../../shared/const'
import http from '../../../../shared/http'
import { fullLogOut, getFormattedDate, stateLabel } from '../../../../shared/helpers'
import { optionLabel } from '../../../../shared/optionList'
import { activeAgainLabel, formatDay } from '../../../../shared/unavailability'
import useScrollTopOnTabChange from '../../../../shared/useScrollTopOnTabChange'
import { juniorAlumniErrors, juniorAlumniPayload } from '../../../../shared/juniorAlumni'

import Btn from '../../../../UI/Btn'
import TabBar from '../../../../UI/TabBar'
import GdprConsentNote from '../../../../UI/GdprConsentNote'
import JuniorAlumniField from '../../../../UI/JuniorAlumniField'
import MiniHeader from '../../../../UI/MiniHeader'
import UnavailableDialog from '../../../../UI/UnavailableDialog'
import { PlainInput, PlainTextarea, PlainSelect } from '../../../../UI/PlainField'
import Loading from '../../../../UI/Loading'
import AvatarField from './AvatarField'
import SupporterNotes from './Notes'

import styles from './style.module.scss'

const TABS = [
	{ key: 'personal', label: 'personalDataAndCompany' },
	{ key: 'competences', label: 'targetGroups' },
	{ key: 'offers', label: 'trainingPortfolio' },
	{ key: 'log', label: 'Comments' }
]

/**
 * Self-Service-Profil einer Unterstuetzer:in.
 *
 * Aufbau folgt dem abgenommenen Mockup: oben der "Profil aktiv"-Schalter und die
 * drei Rollen-Schalter, darunter die Reiter fuer Stammdaten, Kompetenzen und
 * Trainingsangebote.
 */
export default function SupporterProfile() {
	const { t, i18n } = useTranslation()

	// Zielbranchen sind als englische Fassung gespeichert (dieselbe Liste, aus der
	// eine Junior Company ihre Branche wählt) — angezeigt gehört die deutsche.
	const branches = useSelector(state => state.options?.branch) || []

	const [profile, setProfile] = useState(null)
	const [lookups, setLookups] = useState({
		industries: [],
		schoolTypes: [],
		competenceGroups: []
	})
	const [tab, setTab] = useState('personal')

	const tabsRef = useRef(null)
	useScrollTopOnTabChange(tab, tabsRef)
	const [isSaving, setSaving] = useState(false)
	const [ownCompetence, setOwnCompetence] = useState('')
	// Vor dem Pausieren wird nach der Dauer gefragt - siehe UI/UnavailableDialog.
	const [isPauseOpen, setPauseOpen] = useState(false)
	// Meldungen am Abschnitt „Junior Company Teilnahme" (Anforderung #181).
	const [alumniErrors, setAlumniErrors] = useState({})

	const load = () =>
		http
			.get(TApi.SUPPORTER_PROFILE)
			.then(({ data }) => setProfile(data))
			.catch(() => {}) // Meldung kommt aus shared/http

	useEffect(() => {
		load()
		http.get(TApi.SUPPORTER_LOOKUPS)
			.then(({ data }) => setLookups(data))
			.catch(() => {
				// Ohne Stammdaten bleiben die Auswahllisten leer, das Profil ist
				// aber weiter bedienbar.
			})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const catalogCompetences = useMemo(
		() => (lookups.competenceGroups || []).flatMap(group => group.items),
		[lookups]
	)

	if (!profile) return <Loading />

	/*
	 * Rückfallebene für ein anonymisiertes Profil.
	 *
	 * Seit Anforderung #103 beendet der Widerruf den Zugang: Das Konto ist
	 * gesperrt, jede Anfrage antwortet 401, und die Anwendung meldet ab, bevor
	 * diese Seite erscheint. Der Zweig bleibt trotzdem stehen — für Datensätze,
	 * die außerhalb dieses Weges anonymisiert wurden. Ohne ihn stünde hier ein
	 * scheinbar normales, bedienbares Profil: leere Felder, ein Schalter
	 * "Profil aktiv" und der Knopf zum Widerrufen — alles Handlungen, die der
	 * Server mit 400 abweist.
	 */
	if (profile.anonymizedAt) {
		return (
			<div className={styles.root}>
				<MiniHeader className={styles.header} title={t('myProfile')} bg="#f8faff" />
				<section className={styles.deletedNotice}>
					<h3>{t('profileDeletedTitle')}</h3>
					<p>
						{t('profileDeletedText', {
							date: getFormattedDate(profile.gdprConsentWithdrawnAt || profile.anonymizedAt)
						})}
					</p>
					<p>{t('profileDeletedAgain')}</p>
				</section>
			</div>
		)
	}

	const patch = changes => setProfile(current => ({ ...current, ...changes }))

	const save = (changes, successMessage) => {
		setSaving(true)
		return http
			.put(TApi.SUPPORTER_PROFILE, changes)
			.then(({ data }) => {
				setProfile(data)
				if (successMessage !== null) toast.success(successMessage || t('Saved successfully'))
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setSaving(false))
	}

	const toggleRole = role => {
		const next = profile.roles.includes(role)
			? profile.roles.filter(item => item !== role)
			: [...profile.roles, role]

		// Mindestens eine Rolle muss bleiben - ohne Rolle waere das Profil
		// funktionslos und fuer niemanden auffindbar.
		if (!next.length) {
			toast.warning(t('At least one role is required'))
			return
		}

		save({ roles: next })
	}

	/**
	 * Wieder aktiv schalten geht sofort; das Pausieren fragt vorher nach der
	 * Dauer. Ohne diese Frage bleibt ein Profil so lange unsichtbar, bis jemand
	 * daran denkt — mit Enddatum schaltet es sich selbst wieder frei. Beide
	 * Richtungen bestätigt eine E-Mail.
	 */
	const setAvailability = (isActive, unavailableUntil = null) => {
		setSaving(true)
		http.put(TApi.SUPPORTER_AVAILABILITY, {
			isActive,
			...(isActive ? {} : { unavailableUntil })
		})
			.then(({ data }) => {
				setProfile(data)
				setPauseOpen(false)
				toast.success(isActive ? t('profileActiveHint') : t('profileInactiveHint'))
			})
			// Die Meldung kommt aus shared/http — hier bleibt nur aufzuräumen.
			.catch(() => {})
			.finally(() => setSaving(false))
	}

	const toggleAvailability = () => {
		if (profile.availabilityStatus === TSupporterStatus.ACTIVE) {
			setPauseOpen(true)
			return
		}

		setAvailability(true)
	}

	const toggleCompetence = name => {
		const next = profile.competences.includes(name)
			? profile.competences.filter(item => item !== name)
			: [...profile.competences, name]

		patch({ competences: next })
	}

	const addOwnCompetence = () => {
		const value = ownCompetence.trim()
		if (!value || profile.competences.includes(value)) return

		patch({ competences: [...profile.competences, value] })
		setOwnCompetence('')
	}

	const toggleTarget = (field, value) => {
		const current = profile[field] || []
		const next = current.includes(value)
			? current.filter(item => item !== value)
			: [...current, value]

		patch({ [field]: next })
	}

	const selectAllTargets = (field, values) => patch({ [field]: values })

	/*
	 * Widerruf der Einwilligung (Anforderung #59).
	 *
	 * Die Verarbeitung der Unterstuetzer:innen-Daten beruht allein auf der
	 * Einwilligung aus der Registrierung. Der Widerruf laesst ihre Grundlage
	 * entfallen und ist damit dieselbe Handlung wie die Selbstloeschung: das
	 * Backend anonymisiert das Profil und haelt den Widerruf fest.
	 */
	const withdrawConsent = async () => {
		const result = await Swal.fire({
			title: t('withdrawConsent'),
			text: t('withdrawConsentConfirm'),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: t('withdrawConsent'),
			cancelButtonText: t('Cancel'),
			confirmButtonColor: '#d33'
		})

		if (!result.isConfirmed) return

		http.delete(TApi.SUPPORTER_PROFILE)
			.then(() => {
				/*
				 * Der Widerruf beendet den Zugang, nicht nur den Inhalt des
				 * Profils (Anforderung #103): Das Backend sperrt das Konto, die
				 * Sitzung wird hier abgeräumt. Ohne fullLogOut bliebe das
				 * abgelaufene Merkmal im Browser stehen — die nächste Anfrage
				 * liefe in einen 401, statt dass die Anwendung sauber abmeldet.
				 *
				 * Was bisher im Profil zu lesen war, steht jetzt auf der
				 * Anmeldeseite: Sie ist die einzige Seite, die eine Person ohne
				 * Zugang noch erreicht.
				 */
				fullLogOut()
				window.location.href = `${TRoutes.SIGN_IN}?${CONSENT_WITHDRAWN_PARAM}=1`
			})
			.catch(() => {}) // Meldung kommt aus shared/http
	}

	const isActive = profile.availabilityStatus === TSupporterStatus.ACTIVE

	const ownCompetences = profile.competences.filter(item => !catalogCompetences.includes(item))

	/*
	 * labelFor uebersetzt den gespeicherten Wert fuer die Anzeige (z. B.
	 * Bundesland-Enum VIENNA -> "Wien", Anforderung #14). Gespeichert und
	 * verglichen wird weiterhin der Rohwert.
	 */
	/*
	 * Wie renderTargetSection, aber ohne "Alle auswaehlen": Bei einer Vorliebe
	 * ("am liebsten so und dann") hiesse alles auswaehlen so viel wie nichts
	 * auswaehlen.
	 */
	const renderChoiceSection = (label, field, options, labelFor = value => value) => (
		<div className={styles.targetBlock}>
			<span className={styles.targetLabel}>{t(label)}</span>
			<div className={styles.chips}>
				{options.map(option => (
					<button
						key={option}
						type="button"
						className={clsx(
							styles.chip,
							(profile[field] || []).includes(option) && styles.chipActive
						)}
						onClick={() => toggleTarget(field, option)}
					>
						{labelFor(option)}
					</button>
				))}
			</div>
		</div>
	)

	const renderTargetSection = (label, field, options, labelFor = value => value) => {
		/*
		 * Werte, die nicht (mehr) im Katalog stehen. Ueber die Oberflaeche
		 * entstehen sie nicht - sie kamen ueber die Schnittstelle ins Profil und
		 * blieben unsichtbar erhalten (Anforderung #29). Sichtbar gemacht lassen
		 * sie sich mit einem Klick entfernen.
		 */
		const stale = (profile[field] || []).filter(value => !options.includes(value))

		return (
			<div className={styles.targetBlock}>
				<div className={styles.targetHead}>
					<span className={styles.targetLabel}>{t(label)}</span>
					<button
						type="button"
						className={styles.selectAll}
						onClick={() => selectAllTargets(field, options)}
					>
						{t('selectAll')}
					</button>
				</div>
				<div className={styles.chips}>
					{options.map(option => (
						<button
							key={option}
							type="button"
							className={clsx(
								styles.chip,
								(profile[field] || []).includes(option) && styles.chipActive
							)}
							onClick={() => toggleTarget(field, option)}
						>
							{labelFor(option)}
						</button>
					))}
				</div>
				{stale.length > 0 && (
					<>
						<p className={styles.hint}>{t('staleTargetValues')}</p>
						<div className={styles.chips}>
							{stale.map(value => (
								<button
									key={value}
									type="button"
									title={t('removeStaleValue')}
									className={clsx(styles.chip, styles.chipStale)}
									onClick={() => toggleTarget(field, value)}
								>
									{value} ×
								</button>
							))}
						</div>
					</>
				)}
			</div>
		)
	}

	return (
		<div className={styles.root}>
			<MiniHeader className={styles.header} title={t('myProfile')} bg="#f8faff" />

			<div className={styles.statusBar}>
				<div className={styles.statusInfo}>
					<span className={styles.statusLabel}>{t('profileActive')}</span>
					<span className={styles.statusHint}>
						{isActive ? t('profileActiveHint') : t('profileInactiveHint')}
					</span>
					{/*
					 * Waehrend einer Pause steht hier, wann das Profil von selbst
					 * wieder auftaucht — sonst bliebe offen, ob ueberhaupt etwas
					 * hinterlegt ist.
					 */}
					{!isActive && (
						<span className={styles.statusPause}>
							{profile.unavailableUntil
								? t('pauseUntilNote', {
										last: formatDay(profile.unavailableUntil),
										back: activeAgainLabel(profile.unavailableUntil)
								  })
								: t('pauseOpenNote')}
						</span>
					)}
				</div>
				<button
					type="button"
					className={clsx(styles.switch, isActive && styles.switchOn)}
					onClick={toggleAvailability}
					disabled={isSaving}
					aria-label={t('profileActive')}
				>
					<span className={styles.switchKnob} />
				</button>
			</div>

			<section className={styles.roles}>
				<span className={styles.rolesLabel}>{t('possibleRoles')}</span>
				<div className={styles.roleCards}>
					{Object.values(TSupporterRoles).map(role => {
						const enabled = profile.roles.includes(role)
						return (
							<button
								key={role}
								type="button"
								className={clsx(
									styles.roleCard,
									styles[`role${role}`],
									enabled && styles.roleCardOn
								)}
								onClick={() => toggleRole(role)}
								disabled={isSaving}
							>
								<span>{t(`supporterRole${role}`)}</span>
								<span className={clsx(styles.switchSmall, enabled && styles.switchOn)}>
									<span className={styles.switchKnob} />
								</span>
							</button>
						)
					})}
				</div>
			</section>

			{/*
			 * Die gemeinsame Leiste (UI/TabBar) statt einer eigenen: Dieses
			 * Profil hatte als einzige Selbstverwaltung noch ihre eigene, die
			 * bei Beschriftung, Abstand und Tastaturbedienung leicht abwich.
			 */}
			<TabBar className={styles.tabs} tabs={TABS} active={tab} onChange={setTab} tabsRef={tabsRef} />

			<div className={styles.panel}>
				{tab === 'personal' && (
					<>
						<AvatarField
							displayName={profile.displayName}
							avatarHashName={profile.avatarHashName}
							onChange={avatarHashName => patch({ avatarHashName })}
						/>
						<div className={styles.grid}>
							<PlainInput
								label={t('firstName')}
								value={profile.firstName || ''}
								onChange={e => patch({ firstName: e.target.value })}
							/>
							<PlainInput
								label={t('lastName')}
								value={profile.lastName || ''}
								onChange={e => patch({ lastName: e.target.value })}
							/>
							{/*
							 * Die E-Mail-Adresse ist zugleich die Anmeldeadresse und laesst
							 * sich im Profil nicht aendern - sie steht nur zur Ansicht, damit
							 * sichtbar ist, unter welcher Adresse Junior Companies nach einer
							 * Zusage schreiben.
							 */}
							<div>
								<PlainInput
									label={t('email')}
									value={profile.email || ''}
									readOnly
								/>
								<p className={styles.hint}>{t('emailReadOnlyHint')}</p>
							</div>
							<PlainInput
								label={t('company')}
								value={profile.company || ''}
								onChange={e => patch({ company: e.target.value })}
							/>
							<PlainInput
								label={t('position')}
								value={profile.position || ''}
								onChange={e => patch({ position: e.target.value })}
							/>
							<PlainInput
								label={t('phone')}
								value={profile.phone || ''}
								onChange={e => patch({ phone: e.target.value })}
							/>
							<PlainInput
								label="Website"
								value={profile.website || ''}
								onChange={e => patch({ website: e.target.value })}
							/>
							<PlainInput
								label={t('city')}
								value={profile.city || ''}
								onChange={e => patch({ city: e.target.value })}
							/>
							<PlainSelect
								label={t('states')}
								placeholder="—"
								options={StateOptions}
								value={profile.state || ''}
								onChange={e => patch({ state: e.target.value || null })}
							/>
						</div>

						<PlainTextarea
							label={t('Bio')}
							value={profile.bio || ''}
							onChange={e => patch({ bio: e.target.value })}
						/>

						{/*
						 * Junior Company Teilnahme (Anforderung #181) — dieselbe
						 * Frage wie Abschnitt 4 der Registrierung, mit derselben
						 * Pflicht bei „Ja". Wer sich vor der Frage registriert hat,
						 * steht auf „noch nicht angegeben"; gespeichert wird erst
						 * eine echte Antwort.
						 */}
						<h3 className={styles.sectionTitle}>{t('juniorAlumniTitle')}</h3>
						<JuniorAlumniField
							value={profile}
							errors={alumniErrors}
							disabled={isSaving}
							onChange={changes => {
								patch(changes)
								setAlumniErrors({})
							}}
						/>

						{/*
						 * Erreichbarkeit: auf welchem Weg und wann eine Kontaktaufnahme
						 * passt. Junior Companies sehen das schon vor einer Anfrage - die
						 * Kontaktdaten selbst erst nach einer Zusage.
						 */}
						<h3 className={styles.sectionTitle}>{t('contactPreferences')}</h3>
						<p className={styles.hint}>{t('contactPreferencesOwnHint')}</p>
						{renderChoiceSection('contactModes', 'contactModes', TContactModes, value =>
							t(`contactMode${value}`)
						)}
						{renderChoiceSection('contactTimes', 'contactTimes', TContactTimes, value =>
							t(`contactTime${value}`)
						)}
						<PlainTextarea
							label={t('contactNote')}
							rows={2}
							placeholder={t('contactNotePlaceholder')}
							value={profile.contactNote || ''}
							onChange={e => patch({ contactNote: e.target.value })}
						/>

						<label className={styles.checkboxRow}>
							<input
								type="checkbox"
								checked={Boolean(profile.openForNewCompanies)}
								onChange={e => patch({ openForNewCompanies: e.target.checked })}
							/>
							{t('openForNewCompanies')}
						</label>

						{/*
						 * Nur für Profile, die eine Junior Company selbst eingeladen hat.
						 * Wer sich selbst registriert hat oder von Junior Achievement
						 * eingeladen wurde, gehört ohnehin in den allgemeinen Bestand —
						 * dort wäre der Schalter eine Falle, mit der man sich unsichtbar
						 * macht, ohne dass jemand daran denkt.
						 */}
						{Boolean(profile.invitedByCompany) && (
							<>
								<label className={styles.checkboxRow}>
									<input
										type="checkbox"
										checked={Boolean(profile.visibleToAllCompanies)}
										onChange={e => patch({ visibleToAllCompanies: e.target.checked })}
									/>
									{t('visibleToAllCompanies')}
								</label>
								<p className={styles.hint}>
									{profile.visibleToAllCompanies
										? t('visibleToAllCompaniesOn')
										: t('visibleToAllCompaniesOff', { company: profile.invitedByCompany.name })}
								</p>
							</>
						)}

						<div className={styles.footer}>
							<Btn
								disabled={isSaving}
								onClick={() => {
									const errors = juniorAlumniErrors(profile)
									setAlumniErrors(errors)
									if (Object.keys(errors).length) {
										toast.warning(t('juniorAlumniIncomplete'))
										return
									}

									save({
										firstName: profile.firstName,
										lastName: profile.lastName,
										phone: profile.phone,
										company: profile.company,
										position: profile.position,
										website: profile.website,
										city: profile.city,
										state: profile.state,
										bio: profile.bio,
										contactModes: profile.contactModes || [],
										contactTimes: profile.contactTimes || [],
										contactNote: profile.contactNote,
										openForNewCompanies: profile.openForNewCompanies,
										...(profile.invitedByCompany
											? { visibleToAllCompanies: profile.visibleToAllCompanies }
											: {}),
										...juniorAlumniPayload(profile, { keepUnanswered: true })
									})
								}}
							>
								{t('save')}
							</Btn>
						</div>
					</>
				)}

				{tab === 'competences' && (
					<>
						<h3 className={styles.sectionTitle}>{t('competences')}</h3>
						{(lookups.competenceGroups || []).map(group => (
							<div key={group.name} className={styles.targetBlock}>
								<span className={styles.targetLabel}>{group.name}</span>
								<div className={styles.chips}>
									{group.items.map(item => (
										<button
											key={item}
											type="button"
											className={clsx(
												styles.chip,
												profile.competences.includes(item) && styles.chipActive
											)}
											onClick={() => toggleCompetence(item)}
										>
											{item}
										</button>
									))}
								</div>
							</div>
						))}

						<div className={styles.targetBlock}>
							<span className={styles.targetLabel}>{t('furtherCompetences')}</span>
							{ownCompetences.length ? (
								<div className={styles.chips}>
									{ownCompetences.map(item => (
										<button
											key={item}
											type="button"
											className={clsx(styles.chip, styles.chipActive)}
											onClick={() => toggleCompetence(item)}
										>
											{item} ×
										</button>
									))}
								</div>
							) : (
								<p className={styles.hint}>{t('noOwnCompetences')}</p>
							)}
							<div className={styles.addRow}>
								<PlainInput
									placeholder={t('addOwnCompetence')}
									value={ownCompetence}
									onChange={e => setOwnCompetence(e.target.value)}
								/>
								<Btn type="button" onClick={addOwnCompetence}>
									+
								</Btn>
							</div>
						</div>

						<h3 className={styles.sectionTitle}>{t('targetGroups')}</h3>
						{renderTargetSection('schoolTypes', 'targetSchoolTypes', lookups.schoolTypes || [])}
						{renderTargetSection(
							'states',
							'targetStates',
							StateOptions.map(option => option.value),
							stateLabel
						)}
						{/*
							 * Gespeichert ist die englische Fassung (dieselbe, die eine
							 * Junior Company an ihrer Branche trägt) — angezeigt gehört
							 * die deutsche.
							 */}
						{renderTargetSection('industries', 'targetIndustries', lookups.industries || [], value =>
							optionLabel(branches, value, i18n.language)
						)}

						<div className={styles.footer}>
							<Btn
								disabled={isSaving}
								onClick={() =>
									save({
										competences: profile.competences,
										targetStates: profile.targetStates,
										targetSchoolTypes: profile.targetSchoolTypes,
										targetIndustries: profile.targetIndustries
									})
								}
							>
								{t('save')}
							</Btn>
						</div>
					</>
				)}

				{tab === 'offers' && <TrainingOffers t={t} />}

				{tab === 'log' && <SupporterNotes />}
			</div>

			{/*
			 * Einwilligung und ihr Widerruf stehen ausdruecklich im Profil (JAA,
			 * 20.08.2026, Anforderung #59): sichtbar bleibt, worin eingewilligt
			 * wurde und wann - und der Widerruf ist einen Klick entfernt.
			 */}
			<section className={styles.consentZone}>
				<div className={styles.consentHead}>
					<h3 className={styles.consentTitle}>{t('consentTitle')}</h3>
					<span className={styles.consentDate}>
						{profile.gdprConsentAt
							? t('consentGivenAt', { date: getFormattedDate(profile.gdprConsentAt) })
							: t('consentGivenAtUnknown')}
					</span>
				</div>

				<p className={styles.consentText}>{t('gdprConsent')}</p>
				<GdprConsentNote />

				<div className={styles.consentActions}>
					<Btn theme="red" onClick={withdrawConsent}>
						{t('withdrawConsent')}
					</Btn>
					<span className={styles.hint}>{t('withdrawConsentHint')}</span>
				</div>
			</section>

			{isPauseOpen && (
				<UnavailableDialog
					isSaving={isSaving}
					onCancel={() => setPauseOpen(false)}
					onConfirm={until => setAvailability(false, until)}
				/>
			)}
		</div>
	)
}

/** Trainings-Portfolio (Mockup Seite 11). */
function TrainingOffers({ t }) {
	const [offers, setOffers] = useState([])
	const [draft, setDraft] = useState(null)

	const load = () =>
		http
			.get(TApi.SUPPORTER_OFFERS)
			.then(({ data }) => setOffers(data))
			.catch(() => {}) // Meldung kommt aus shared/http

	useEffect(() => {
		load()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const submit = () => {
		if (!draft.title || draft.title.trim().length < 2) {
			toast.warning(t('Required field'))
			return
		}

		const payload = {
			title: draft.title,
			description: draft.description || null,
			durationHours: draft.durationHours ? Number(draft.durationHours) : null,
			format: draft.format || 'ONLINE',
			status: draft.status || 'ACTIVE',
			competences: draft.competences || []
		}

		const promise = draft.id
			? http.put(TApi.SUPPORTER_OFFER_ID(draft.id), payload)
			: http.post(TApi.SUPPORTER_OFFERS, payload)

		promise
			.then(() => {
				toast.success(t('Saved successfully'))
				setDraft(null)
				load()
			})
			.catch(() => {}) // Meldung kommt aus shared/http
	}

	const remove = id =>
		http
			.delete(TApi.SUPPORTER_OFFER_ID(id))
			.then(() => {
				toast.success(t('Saved successfully'))
				load()
			})
			.catch(() => {}) // Meldung kommt aus shared/http

	return (
		<>
			<div className={styles.offerHead}>
				<h3 className={styles.sectionTitle}>{t('trainingPortfolio')}</h3>
				<Btn onClick={() => setDraft({ format: 'ONLINE', status: 'ACTIVE' })}>
					+ {t('addTraining')}
				</Btn>
			</div>

			<div className={styles.offerGrid}>
				{offers.map(offer => (
					<article key={offer.id} className={styles.offerCard}>
						<span className={styles.offerStatus}>{t(`supporterStatus${offer.status}`)}</span>
						<h4 className={styles.offerTitle}>{offer.title}</h4>
						<span className={styles.offerMeta}>
							{offer.durationHours ? `${offer.durationHours} h` : ''}
							{offer.durationHours ? ' · ' : ''}
							{t(`format${offer.format}`)}
						</span>
						<div className={styles.offerActions}>
							<Btn onClick={() => setDraft(offer)}>
								{t('edit')}
							</Btn>
							<Btn theme="red" onClick={() => remove(offer.id)}>
								{t('delete')}
							</Btn>
						</div>
					</article>
				))}
			</div>

			{draft && (
				<div className={styles.offerForm}>
					<PlainInput
						label={t('trainingTitle')}
						value={draft.title || ''}
						onChange={e => setDraft({ ...draft, title: e.target.value })}
					/>
					<PlainTextarea
						label={t('description')}
						value={draft.description || ''}
						onChange={e => setDraft({ ...draft, description: e.target.value })}
					/>
					<div className={styles.grid}>
						<PlainInput
							label={t('trainingDuration')}
							type="number"
							value={draft.durationHours || ''}
							onChange={e => setDraft({ ...draft, durationHours: e.target.value })}
						/>
						<PlainSelect
							label={t('trainingFormat')}
							options={['ONLINE', 'PRESENCE', 'HYBRID'].map(value => ({
								value,
								label: t(`format${value}`)
							}))}
							value={draft.format || 'ONLINE'}
							onChange={e => setDraft({ ...draft, format: e.target.value })}
						/>
					</div>
					<div className={styles.footer}>
						<Btn theme="grey" onClick={() => setDraft(null)}>
							{t('Cancel')}
						</Btn>
						<Btn onClick={submit}>{t('save')}</Btn>
					</div>
				</div>
			)}
		</>
	)
}
